import { chunk } from "@suse-o11y/chunk";
import { filter } from "@suse-o11y/filter";
import { fromEntries } from "@suse-o11y/from-entries";
import { isEmpty } from "@suse-o11y/is-empty";
import { isUndefined } from "@suse-o11y/is-undefined";
import type { NonEmptyArray } from "@suse-o11y/non-empty-array";
import type { Object } from "@suse-o11y/object";
import { omitBy } from "@suse-o11y/omit-by";
import { piped } from "@suse-o11y/piped";
import { times } from "@suse-o11y/times";
import { type RawCreateParams, type ZodRawShape, type ZodTypeAny, z } from "zod";

function partial<T extends Object>(input: T) {
	const sanitized = omitBy(input, isUndefined);

	return isEmpty(sanitized) ? undefined : sanitized;
}

/**
 * Zod.tuple does not support optional elements.
 * The custom tuple schema makes sure that the incoming tuple has the undefined placeholders for missing elements.
 */
function tuple<T extends [] | NonEmptyArray<ZodTypeAny, "writable">>(schemas: T, params?: RawCreateParams) {
	return z
		.array(z.unknown())
		.transform((elements) => [...elements, ...times(schemas.length - elements.length, () => undefined)])
		.pipe(z.tuple(schemas, params));
}

/**
 * Decode the object from a single dimensional Array with keys on even and values on odd positions.
 * Trailing odd-length chunks (malformed input) are dropped.
 */
function object<T extends ZodRawShape>(schema: T) {
	return z
		.array(z.unknown())
		.transform(
			piped(
				chunk(2),
				filter((entry) => entry.length === 2),
				fromEntries,
			),
		)
		.pipe(z.object(schema));
}

export { object, partial, tuple };
